import React, { useState, useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import Typewriter from "typewriter-effect";
import micIcon from '../assets/mic_icon.png';
import sendIcon from "../assets/send_icon.png";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import script from '../script_publicidade.json';
import { app, db } from "../config/firebaseConfig";
import { doc, setDoc } from "firebase/firestore";
import { Oval } from "react-loader-spinner";
import { httpsCallable, getFunctions } from "firebase/functions";
import { fetchSignedVideos } from '../utils/videoApi';

const videoSequence = ["intro", "p1", "p2", "p3", "p4", "p5", "p6", "p7", "p8", "p9"];
const autoPlayApos = ["p3", "p4", "p5", "p7"];
const respostasObrigatorias = ["intro", "p1", "p2", "p6", "p8", "p9"];

export default function Publicidade() {
  const [videoIndex, setVideoIndex] = useState(0);
  const [mensagens, setMensagens] = useState([]);
  const [respostaUsuario, setRespostaUsuario] = useState("");
  const [esperandoResposta, setEsperandoResposta] = useState(false);
  const [videoSpeed, setVideoSpeed] = useState(1.25);
  const [isListening, setIsListening] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimer = useRef(null);
  const videoRef = useRef(null);
  const inputRef = useRef(null);
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const exibidosRef = useRef(new Set());
  const [protocoloID, setProtocoloID] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const messageEndRef = useRef(null);
  const [isMuted, setIsMuted] = useState(videoIndex === 0);
  const [videoUrls, setVideoUrls] = useState({});
  const functions = useMemo(() => getFunctions(app, "southamerica-east1"), []);

  const generateProtocol = () => {
    return Math.floor(10000000 + Math.random() * 90000000).toString();
  };

  useEffect(() => {
    let isMounted = true;
    const loadVideos = async () => {
      const videos = await fetchSignedVideos('eia/media/publicidade');
      if (!isMounted) return;
      setVideoUrls(videos);
    };
    loadVideos();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setIsMuted(videoIndex === 0);
  }, [videoIndex]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = videoSpeed;
    }
  }, [videoSpeed, videoIndex]);

  useEffect(() => {
    if (listening) {
      let texto = transcript;
      const ultimaPerguntaIA = [...mensagens].reverse().find(msg => msg.tipo === 'ia')?.texto || "";
  
      if (ultimaPerguntaIA.includes("e-mail")) {
        texto = texto
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\s+/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9@._-]/g, "");
      }
  
      setRespostaUsuario(texto);
    }
  }, [transcript, listening, mensagens]);

  useEffect(() => {
    const atual = videoSequence[videoIndex];
    const textoIA = obterTextoIA(atual);
  
    if (textoIA && !exibidosRef.current.has(atual)) {
      exibidosRef.current.add(atual);
      setMensagens((prev) => [...prev, { tipo: "ia", texto: textoIA }]);
    }
  }, [videoIndex]);

  const toggleMic = () => {
    if (isListening) {
      SpeechRecognition.stopListening();
      clearInterval(recordingTimer.current);
    } else {
      SpeechRecognition.startListening({ continuous: true, language: 'pt-BR' });
      setRecordingTime(0);
      recordingTimer.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
    setIsListening(!isListening);
    resetTranscript();
  };

  const saveToFirebase = async (mensagensCompletas = mensagens) => {
    const id = protocoloID || generateProtocol();
    setProtocoloID(id);
    setIsLoading(true);
  
    const formattedRespostas = [];
    let lastIa = null;

    for (let i = 0; i < mensagensCompletas.length; i++) {
      const msg = mensagensCompletas[i];

      if (msg.tipo === "ia") {
        lastIa = msg.texto;
      } else if (msg.tipo === "user" && lastIa) {
        formattedRespostas.push({
          pergunta: lastIa,
          resposta: msg.texto,
        });
        lastIa = null; 
      }
    }
  
    try {
      await setDoc(doc(db, "publicidade", id), {
        protocoloID: id,
        respostas: formattedRespostas,
        timestamp: new Date().toISOString()
      });

      const sendResumoPublicidade = httpsCallable(functions, 'sendResumoPublicidade');
      try {
        await sendResumoPublicidade({
          mensagens: formattedRespostas,
          protocoloID: id
        });
        console.log("✅ Resumo enviado para contato@due.app.br");
      } catch (error) {
        console.error("❌ Erro ao enviar resumo:", error);
      }
  
      setShowConfirmationModal(true);
    } catch (err) {
      console.error("Erro ao salvar no Firestore:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVideoEnd = () => {
    const atual = videoSequence[videoIndex];
    if (autoPlayApos.includes(atual)) {
      proximoVideo();
    } else if (respostasObrigatorias.includes(atual)) {
      setEsperandoResposta(true);
    } else {
      proximoVideo();
    }
  };

  const proximoVideo = () => {
    setVideoIndex((prev) => prev + 1);
    setEsperandoResposta(false);
    setRespostaUsuario("");
  };

  const enviarResposta = async (e) => {
    if (e) e.preventDefault();
    if (!respostaUsuario.trim()) return;
    const resposta = respostaUsuario;
    setRespostaUsuario("");
    setEsperandoResposta(false);

    setMensagens((prev) => {
      const novaLista = [...prev, { tipo: "user", texto: resposta }];

      if (videoIndex === videoSequence.length - 2) {
        setTimeout(() => {
          saveToFirebase(novaLista); 
        }, 100);
      }
  
      return novaLista;
    });

    proximoVideo();
  };

  useEffect(() => {
      const interval = setInterval(() => {
          if (messageEndRef.current) {
            messageEndRef.current.scrollTop = messageEndRef.current.scrollHeight;
          }
      }, 100);
  
      return () => clearInterval(interval);
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const obterTextoIA = (id) => {
    if (id === "intro") return script.entrevista[0].intro;
    for (let i = 1; i < script.entrevista.length; i++) {
      const seção = script.entrevista[i];
      if (seção.perguntas) {
        for (let pergunta of seção.perguntas) {
          if (pergunta.id === parseInt(id.replace("p", ""))) {
            return pergunta.texto;
          }
        }
      }
    }
    return "";
  };

  return (
    <Wrapper>
      <PageContainer>
        <SpeedSelectorContainer>
          <p>Velocidade:</p>
          {[1, 1.25, 1.5, 2].map((v) => (
            <SpeedButton key={v} onClick={() => setVideoSpeed(v)} selected={videoSpeed === v}>
              {v}x
            </SpeedButton>
          ))}
        </SpeedSelectorContainer>

        <video
          ref={videoRef}
          autoPlay
          muted={isMuted}
          onEnded={handleVideoEnd}
          src={videoUrls[videoSequence[videoIndex]] || ''}
        />

        {isMuted && videoIndex === 0 && (
          <UnmuteButton onClick={() => setIsMuted(false)}>
            Ativar som 🔊
          </UnmuteButton>
        )}

        <MessageContainer ref={messageEndRef}>
          {mensagens.map((msg, i) => (
            <MessageBox key={i} type={msg.tipo}>
              <Message>
                {msg.tipo === "ia" ? (
                  <Typewriter
                    onInit={(typewriter) => {
                      typewriter.typeString(msg.texto).start();
                    }}
                    options={{ delay: 35, cursor: "" }}
                  />
                ) : (
                  msg.texto
                )}
              </Message>
            </MessageBox>
          ))}
        </MessageContainer>

        <InteractiveContainer>
        <form onSubmit={(e) => {
            if (esperandoResposta) enviarResposta(e);
            else e.preventDefault(); 
        }}>
            <StyledInput
              ref={inputRef}
              value={respostaUsuario}
              onChange={(e) => setRespostaUsuario(e.target.value)}
              placeholder={esperandoResposta ? "Digite sua resposta..." : "Aguarde..."}
            />
            <SendButton type="submit" disabled={!esperandoResposta} isListening={isListening}>
              <img src={sendIcon} alt="send" />
            </SendButton>
          </form>
          <MicTimer isListening={isListening}>
            <MicButton onClick={toggleMic} isListening={isListening}>
              <img src={micIcon} alt="mic" />
            </MicButton>
            {isListening && <TimerContainer>{formatTime(recordingTime)}</TimerContainer>}
          </MicTimer>
        </InteractiveContainer>
      </PageContainer>
      {isLoading && (
            <Modal>
                <ModalContent>
                <Oval height={60} width={60} color="#000" secondaryColor="#ccc" strokeWidth={4} />
                <p style={{ marginTop: 20 }}>Salvando suas respostas...</p>
                </ModalContent>
            </Modal>
            )}

            {showConfirmationModal && (
            <Modal>
                <ModalContent>
                <p>Respostas enviadas com sucesso! Protocolo: {protocoloID}</p>
                <button onClick={() => setShowConfirmationModal(false)}>Fechar</button>
                </ModalContent>
            </Modal>
        )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fb;
`;

const PageContainer = styled.div`
  width: 500px;
  height: 720px;
  background-color: #FCFEFF;
  box-shadow: 20px 20px 20px #BACCDE8A;
  border: 1px solid #D1D1D1;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  position: relative;
  video {
    position: absolute;
    width: 1300px;
    border-radius: 16px;
    object-fit: cover;
  }

  @media only screen and (max-width: 480px) {
    width: 100%;
    min-width: 320px;
    padding: 0;
    border-radius: 10px;
    box-shadow: none;
  }
`

const StyledInput = styled.input.attrs(props => ({
  'data-islistening': props.isTranscribing
}))`
  width: 310px;
  height: 52px;
  z-index: 2;
  border-radius: 16px;
  border: none;
  background-color: rgba(0, 0, 0, 0.5);
  color: #FFF;
  padding-left: 16px;
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  text-align: left; 
  &:focus {
      outline: 1px solid #000;
  }

  @media only screen and (max-width: 480px) {
      width: 200px;
      height: 40px;
      font-size: 16px;
      padding-left: 10px;
  }
`;

const MessageContainer = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px; 
  margin-bottom: 90px;
  max-height: 260px;
  overflow-y: auto; 
  width: 90%;
  z-index: 4;
  &::-webkit-scrollbar {
      width: 0;
      display: none; 
  }
`;

const MessageBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${props => props.type === 'ia' ? 'flex-start' : 'flex-end'};
`

const Message = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 18px;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  height: auto;
  padding: 15px; 
  border-radius: 12px; 
  width: fit-content;
`;

const InteractiveContainer = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  left: 22px;
  bottom: 20px;

  form {
      display: flex;
      align-items: center;
  }
`
const MicButton = styled.button`
  height: 52px;
  width: 52px;
  border-radius: 16px;
  border: none;
  background-color: ${props => props.isListening ? 'green' : 'rgba(0, 0, 0, 0.5)'};
  margin-left: 20px;
  cursor: pointer;

  @media only screen and (max-width: 480px) {
      width: 40px;
      height: 40px;
      margin-left: 5px;

      img {
          width: 24px;
          height: 24px;
      }
  }
`

const SendButton = styled.button`
    height: 52px;
    width: 52px;
    border-radius: 16px;
    border: none;
    background-color:  ${({ isListening }) => isListening ? 'red' : 'rgba(0, 0, 0, 0.5)'};
    margin-left: 10px;
    cursor: ${({ isListening }) => isListening ? 'not-allowed' : 'pointer'};
    pointer-events: ${({ isListening }) => isListening ? 'none' : 'auto'};

    @media only screen and (max-width: 480px) {
        width: 40px;
        height: 40px;
        margin-left: 5px;

        img {
        width: 24px;
        height: 24px;
        }
    }
`

const TimerContainer = styled.div`
    font-family: 'Roboto', sans-serif;
    width: 40px;
    font-size: 14px;
    margin-left: 20px;
    color: #fff;
    text-align: center;
    margin-top: 5px;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 4px;
    padding: 2px 2px;
`;

const MicTimer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: ${({ isListening }) => (isListening ? '20px' : '0')};
    transition: margin-top 0.3s ease;
`

const SpeedSelectorContainer = styled.div`
    font-family: 'Roboto', sans-serif;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 5px 10px;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 12px;
    margin-bottom: 10px;
    position: absolute;
    top: 10px;
    left: 120px;
    transform: translateX(-50%);
    z-index: 10;
    
    p {
        color: white;
        font-size: 12px;
        font-weight: 500;
        margin: 0;
    }
`;

const SpeedButton = styled.button`
    background-color: ${(props) => (props.selected ? "#28a745" : "rgba(255, 255, 255, 0.2)")};
    color: white;
    border: none;
    padding: 4px 6px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 10px;
    transition: background-color 0.3s ease, transform 0.2s;

    &:hover {
        background-color: #218838;
        transform: scale(1.1);
    }
`;

const Modal = styled.div`
  position: fixed;
  top: 0; left: 0; width: 100vw; height: 100vh;
  display: flex; justify-content: center; align-items: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  button {
    margin-top: 20px;
    padding: 10px 20px;
    border: none;
    background: #000;
    color: #fff;
    border-radius: 8px;
    cursor: pointer;
    &:hover {
      background: #333;
    }
  }
`;

const UnmuteButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0,0,0,0.7);
  color: #fff;
  border: none;
  padding: 10px 16px;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  z-index: 5;

  &:hover {
    background-color: rgba(0,0,0,0.9);
  }

  @media only screen and (max-width: 480px) {
    top: 10px;
    right: 10px;
    padding: 8px 12px;
    font-size: 12px;
  }
`;
